import { useState } from 'react';
import { Button } from './ui/button';
import { MapPin, Navigation, Map as MapIcon, Globe } from 'lucide-react';

interface MapProps {
  center?: [number, number];
  zoom?: number;
  markerText?: string;
  address?: string;
}

const Map = ({ 
  center = [36.638418, -3.385982], 
  zoom = 14, 
  markerText = 'Kijani Arusha Airport Lodge',
  address = 'JJ7Q+H9, B, Olasiti, Burka 23119, Arusha, Tanzania'
}: MapProps) => {
  const [showDirections, setShowDirections] = useState(false);

  const lat = center[1];
  const lng = center[0];

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  const googleMapsEmbedUrl = `https://maps.google.com/maps?q=Kijani+Safari+Lodge,+Arusha,+Tanzania&z=${zoom}&output=embed`;

  const openDirections = () => {
    if (isMobile) {
      const mapsUrl = isIOS 
        ? `http://maps.apple.com/?daddr=${lat},${lng}`
        : `geo:${lat},${lng}?q=${lat},${lng}(${encodeURIComponent(markerText)})`;
      window.location.href = mapsUrl;
    } else {
      setShowDirections(!showDirections);
    }
  };

  const openInGoogleMaps = () => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
  };

  const openInAppleMaps = () => {
    window.open(`http://maps.apple.com/?daddr=${lat},${lng}`, '_blank');
  };

  const openInWaze = () => {
    window.open(`https://waze.com/ul?ll=${lat},${lng}&navigate=yes`, '_blank');
  };

  return (
    <div className="relative w-full h-full min-h-[250px] rounded-lg overflow-hidden">
      <iframe
        src={googleMapsEmbedUrl}
        className="w-full h-full min-h-[250px] border-0"
        allowFullScreen
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        title={`Map showing location of ${markerText}`}
      />
      
      {/* Address Overlay - Top Left */}
      <div className="absolute top-4 left-4 bg-card/95 backdrop-blur-sm border border-border shadow-lg rounded-lg p-3 max-w-xs z-10">
        <div className="flex items-start gap-2">
          <MapPin className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-sm text-foreground">{markerText}</p>
            <p className="text-xs text-muted-foreground mt-1">{address}</p>
          </div>
        </div>
      </div>

      {/* Directions Button - Bottom Center */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
        <div className="relative">
          <Button
            onClick={openDirections}
            className="shadow-lg bg-primary hover:bg-primary/90 transition-all duration-200"
          >
            <Navigation className="h-4 w-4 mr-2" />
            {isMobile ? 'Navigate' : 'Get Directions'}
          </Button>
          
          {showDirections && !isMobile && (
            <div className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 bg-card/98 backdrop-blur-md border border-border shadow-2xl rounded-xl p-3 min-w-[220px] animate-fade-in">
              <div className="flex flex-col gap-2">
                <div className="text-xs font-medium text-muted-foreground px-2 pb-1 border-b border-border/50">
                  Choose your map app
                </div>
                <Button onClick={openInGoogleMaps} variant="ghost" size="sm" className="justify-start hover:bg-primary/10 hover:text-primary transition-all duration-200 group">
                  <div className="h-5 w-5 mr-3 flex items-center justify-center rounded-md bg-[#4285F4] text-white group-hover:scale-110 transition-transform">
                    <Globe className="h-3 w-3" />
                  </div>
                  <span className="font-medium">Google Maps</span>
                </Button>
                <Button onClick={openInAppleMaps} variant="ghost" size="sm" className="justify-start hover:bg-primary/10 hover:text-primary transition-all duration-200 group">
                  <div className="h-5 w-5 mr-3 flex items-center justify-center rounded-md bg-[#007AFF] text-white group-hover:scale-110 transition-transform">
                    <MapIcon className="h-3 w-3" />
                  </div>
                  <span className="font-medium">Apple Maps</span>
                </Button>
                <Button onClick={openInWaze} variant="ghost" size="sm" className="justify-start hover:bg-primary/10 hover:text-primary transition-all duration-200 group">
                  <div className="h-5 w-5 mr-3 flex items-center justify-center rounded-md bg-[#33CCFF] text-white group-hover:scale-110 transition-transform">
                    <Navigation className="h-3 w-3" />
                  </div>
                  <span className="font-medium">Waze</span>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Map;
